import { useCallback, useRef, useState } from 'react';

import { endMeeting } from '../services/meetingEnd';

/**
 * The confirm-then-end interaction shared by the Meetings list and the
 * meeting details page: request an end (which opens the confirmation),
 * cancel it, or confirm it. The actual ending - and its
 * backend-is-the-source-of-truth handling, including the race where the
 * reaper ended the meeting first - lives in services/meetingEnd.js.
 *
 * Usage: render <EndMeetingModal> while `endTarget` is set.
 *
 * `onSettled({ ended, meeting, target })` runs once the attempt finished,
 * so the caller can update its own copy of the meeting: `meeting` is the
 * freshly re-read record (or null), `target` is what was passed to
 * requestEnd. Nothing is assumed ended by this hook itself.
 */
export function useEndMeeting({ onSettled } = {}) {
  const [endTarget, setEndTarget] = useState(null);
  const [isEnding, setIsEnding] = useState(false);
  const [endError, setEndError] = useState('');
  // A ref as well as state: two quick confirms must never send two ends.
  const inFlightRef = useRef(false);

  const requestEnd = useCallback((target) => {
    setEndError('');
    setEndTarget(target);
  }, []);

  const cancelEnd = useCallback(() => {
    if (inFlightRef.current) return;
    setEndTarget(null);
  }, []);

  const confirmEnd = useCallback(async () => {
    if (!endTarget || inFlightRef.current) return;

    inFlightRef.current = true;
    setIsEnding(true);
    setEndError('');

    const target = endTarget;
    const result = await endMeeting(target.id);

    inFlightRef.current = false;
    setIsEnding(false);
    setEndTarget(null);
    if (!result.ended) setEndError(result.errorMessage);

    if (onSettled) onSettled({ ...result, target });
  }, [endTarget, onSettled]);

  return {
    endTarget,
    // The id whose end request is in flight right now, for disabling that
    // meeting's own End button.
    endingId: isEnding && endTarget ? endTarget.id : null,
    isEnding,
    endError,
    requestEnd,
    cancelEnd,
    confirmEnd,
  };
}
